import React, { useState, useEffect, useRef } from 'react';
import useUserStore from '../../stores/useUserStore';
import useChatStore from '../../stores/useChatStore';
import FlerpieChart from './FlerpieChart';
import FlerpieNavigateButton from './FlerpieNavigateButton';
import FlerpieFeedback from './FlerpieFeedback';
import FlerpieNoteButton from './FlerpieNoteButton';
import FlerpieRememberLine from './FlerpieRememberLine';
import FlerpiePdfButton from './FlerpiePdfButton';

// Only an answer the server stored can take a verdict; error bubbles carry a local id.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Simple markdown-ish renderer. Handles bold, italic, inline code,
 * code blocks, and bullet lists. No external dependency needed.
 */
const renderMarkdown = (text) => {
  if (!text) return null;

  // Strip HTML tags as XSS defense-in-depth (React escapes too, but belt + suspenders)
  const lines = text.replace(/<[^>]*>/g, '').split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeBlockLines = [];

  const processInline = (line, i) => {
    // Process bold, italic, inline code
    const parts = [];
    let remaining = line;
    let key = 0;

    // Simple regex-based inline processing
    const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(remaining)) !== null) {
      // Text before match
      if (match.index > lastIndex) {
        parts.push(remaining.slice(lastIndex, match.index));
      }
      const m = match[0];
      if (m.startsWith('`')) {
        parts.push(
          <code
            key={key++}
            className="px-1.5 py-0.5 rounded text-[11px] font-mono"
            style={{ background: 'rgba(255,204,51,0.08)', color: '#ffcc33' }}
          >
            {m.slice(1, -1)}
          </code>
        );
      } else if (m.startsWith('**')) {
        parts.push(<strong key={key++} className="font-semibold text-gray-100">{m.slice(2, -2)}</strong>);
      } else if (m.startsWith('*')) {
        parts.push(<em key={key++} className="italic text-gray-300">{m.slice(1, -1)}</em>);
      }
      lastIndex = match.index + m.length;
    }

    if (lastIndex < remaining.length) {
      parts.push(remaining.slice(lastIndex));
    }

    return parts.length > 0 ? parts : remaining;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code block toggle
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre
            key={`code-${i}`}
            className="rounded-lg px-3 py-2.5 my-1.5 text-[11px] font-mono overflow-x-auto"
            style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,204,51,0.06)',
              color: '#d4d4d4',
            }}
          >
            <code>{codeBlockLines.join('\n')}</code>
          </pre>
        );
        codeBlockLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    // Bullet lists
    if (line.match(/^[\s]*[-•*]\s/)) {
      const content = line.replace(/^[\s]*[-•*]\s/, '');
      elements.push(
        <div key={i} className="flex gap-2 pl-1">
          <span className="text-primary/60 mt-px">•</span>
          <span>{processInline(content, i)}</span>
        </div>
      );
      continue;
    }

    // Empty lines
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
      continue;
    }

    // Regular line
    elements.push(<div key={i}>{processInline(line, i)}</div>);
  }

  return elements;
};

const MemoizedContent = React.memo(({ content, isStreaming, isNew }) => {
  const [visibleLength, setVisibleLength] = useState(isNew ? 0 : (content || '').length);
  const targetLengthRef = useRef((content || '').length);
  const intervalRef = useRef(null);
  const hasNotifiedComplete = useRef(false);
  const doneTyping = visibleLength >= targetLengthRef.current && targetLengthRef.current > 0;

  // Notify store when typing animation finishes
  useEffect(() => {
    if (doneTyping && isNew && !hasNotifiedComplete.current) {
      hasNotifiedComplete.current = true;
      useChatStore.getState().onTypingComplete();
    }
  }, [doneTyping, isNew]);

  useEffect(() => {
    if (!content) return;
    targetLengthRef.current = content.length;

    // If already fully revealed, nothing to do
    if (visibleLength >= content.length) return;

    // Start typing interval if not running
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setVisibleLength(prev => {
          if (prev >= targetLengthRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return prev;
          }
          return prev + 4;
        });
      }, 15);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayText = (content || '').slice(0, visibleLength);
  const rendered = React.useMemo(() => renderMarkdown(displayText), [displayText]);
  const showCursor = isStreaming || !doneTyping;

  const cursor = (
    <span
      className="inline-block w-1.5 h-3.5 ml-0.5 rounded-sm"
      style={{
        background: '#ffcc33',
        animation: 'flerpie-cursor-blink 0.8s ease-in-out infinite',
        verticalAlign: 'text-bottom',
      }}
    />
  );

  // Append cursor inline at the end of the last rendered element
  if (showCursor && rendered && rendered.length > 0) {
    const last = rendered[rendered.length - 1];
    const withCursor = React.cloneElement(last, {}, ...(Array.isArray(last.props.children) ? last.props.children : [last.props.children]), cursor);
    return (
      <div className="space-y-0.5">
        {rendered.slice(0, -1)}
        {withCursor}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {displayText ? rendered : null}
      {showCursor && !displayText ? cursor : null}
    </div>
  );
});

const FlerpieMessageBubble = ({ message, isStreaming = false }) => {
  const user = useUserStore((s) => s.user);
  const progressLabel = useChatStore((s) => (isStreaming ? s.progressLabel : null));
  const shared = useChatStore((s) => !!(s.activeThread && s.activeThread.shared));
  const isUser = message.role === 'user';
  const isAdmin = user.isadmin || user.can_admin;
  const [showWork, setShowWork] = useState(false);

  // User message. In a shared thread, someone else's sits on the left under their name.
  if (isUser) {
    // Someone else's question sits on the left under their name, even after they left the thread.
    const theirs = !!message.author_user_id && message.author_user_id !== user.id && (shared || !!message.author_label);
    return (
      <div className={`flex ${theirs ? 'justify-start' : 'justify-end'}`}>
        <div className="max-w-[85%]">
          {theirs && <div className="text-[10px] text-gray-500 mb-0.5 ml-1">{message.author_label || 'Someone'}</div>}
          <div
            className={`px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${theirs ? 'rounded-bl-md' : 'rounded-br-md'}`}
            style={{
              background: theirs ? 'rgba(255,255,255,0.06)' : 'rgba(255,204,51,0.12)',
              color: '#e5e5e5',
            }}
          >
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%]">
        <div
          className="px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[13px] leading-relaxed"
          style={{
            background: 'rgba(58,58,58,0.6)',
            color: '#d4d4d4',
          }}
        >
          {/* What Flerpie is checking, until the first word of the answer arrives */}
          {isStreaming && !message.content && progressLabel && (
            <div className="text-[11px] italic text-gray-500 mb-1">{progressLabel}...</div>
          )}

          {/* Message content or streaming state */}
          <MemoizedContent content={message.content} isStreaming={isStreaming} isNew={isStreaming || message._isNew} />

          {/* Visualization */}
          {message.visualization && <FlerpieChart visualization={message.visualization} />}

          {/* Opens the PDF tool page for this answer in a new tab. */}
          {message.visualization && !isStreaming && (
            <FlerpiePdfButton message={message} />
          )}

          {/* The page Flerpie offered to open. Nothing navigates until clicked. */}
          {message.navigate && !isStreaming && <FlerpieNavigateButton navigate={message.navigate} />}

          {/* The note Flerpie offered to carry. Nothing is sent until clicked. */}
          {message.note && message.note.to_label && !isStreaming && (!message.note.from_user_id || message.note.from_user_id === user.id) && <FlerpieNoteButton messageId={message.id} note={message.note} />}
          {message.note && message.note.to_label && !isStreaming && message.note.from_user_id && message.note.from_user_id !== user.id && (
            <div className="mt-1.5 text-[10px] text-gray-500">A note for {message.note.to_label}; only the person who asked can send it.</div>
          )}

          {/* What answered, from the server's record of what ran, never from the model. */}
          {Array.isArray(message.sources) && message.sources.length > 0 && !isStreaming && (
            <div className="mt-1.5 text-[10px] text-gray-500">From {message.sources.join(', ')}.</div>
          )}

          {message.remember && !isStreaming && <FlerpieRememberLine messageId={message.id} remember={message.remember} />}
        </div>

        {/* Was it helpful? One verdict per person per answer. */}
        {UUID_RE.test(String(message.id || '')) && !isStreaming && (
          <FlerpieFeedback messageId={message.id} feedback={message.feedback || null} />
        )}

        {/* Show Flerpie's work — admin only */}
        {isAdmin && message.sql_generated && !isStreaming && (
          <div className="mt-1 ml-1">
            <button
              onClick={() => setShowWork(!showWork)}
              className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors flex items-center gap-1"
            >
              <svg
                width="10" height="10" viewBox="0 0 10 10"
                className={`transition-transform duration-200 ${showWork ? 'rotate-90' : ''}`}
              >
                <path d="M3 1L7 5L3 9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
              Show Flerpie's work
            </button>
            {showWork && (
              <pre
                className="mt-1.5 rounded-lg px-3 py-2 text-[10px] font-mono overflow-x-auto"
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,204,51,0.04)',
                  color: '#888',
                  maxHeight: 120,
                }}
              >
                {message.sql_generated}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlerpieMessageBubble;
